import { Component, DestroyRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyModel } from 'src/app/models/company.model';
import { LicenseModel } from 'src/app/models/license.model';
import { CompanyService } from 'src/app/services/companies-services/company.service';
import { LicenseService } from 'src/app/services/licenses-service/license.service';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface ResultOption {
  key: string;
  label: string;
}

interface ResultGroup {
  label: string;
  options: ResultOption[];
}

@Component({
    selector: 'app-header-search',
    templateUrl: './header-search.component.html',
    styleUrls: ['./header-search.component.scss'],
    standalone: false
})
export class HeaderSearchComponent implements OnInit {
  query = '';
  groups: ResultGroup[] = [];
  private companies: CompanyModel[] = [];
  private licenses: LicenseModel[] = [];

  constructor(
    private companyService: CompanyService,
    private licenseService: LicenseService,
    private router: Router,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit() {
    this.loadSearchData();
  }

  public onQueryChange(query: string) {
    this.query = query ?? '';
    this.buildGroups();
  }

  public onSelectResult(key: string) {
    if (!key) {
      return;
    }
    const [type, internalId] = key.split(':');
    if (type === 'company') {
      this.router.navigate(['/company', internalId]);
    } else if (type === 'license') {
      this.router.navigate(['/license', internalId]);
    }
    this.query = '';
    this.groups = [];
  }

  private loadSearchData() {
    this.companyService.getCompanies().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: CompanyModel[]) => this.companies = res || []),
      catchError(() => {
        this.companies = [];
        return of([]);
      })
    ).subscribe();

    this.licenseService.getLicenses().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: LicenseModel[]) => this.licenses = res || []),
      catchError(() => {
        this.licenses = [];
        return of([]);
      })
    ).subscribe();
  }

  private buildGroups() {
    const tokens = this.normalize(this.query).split(/\s+/).filter((token: string) => token.length > 0);
    if (tokens.length === 0) {
      this.groups = [];
      return;
    }

    const companyMatches = this.companies
      .filter((c: CompanyModel) => this.matchesTokens(tokens, [c.companyName, c.companyCif]))
      .map((c: CompanyModel) => ({
        key: `company:${c.internalId}`,
        label: `${c.companyName} (${c.companyCif || 'sin CIF'})`
      }));

    const matchedLicenses = this.licenses.filter((l: LicenseModel) =>
      this.matchesTokens(tokens, [l.description, l.companyName])
    );

    const byCompany = new Map<string, ResultOption[]>();

    matchedLicenses.forEach((l: LicenseModel) => {
      const option: ResultOption = {
        key: `license:${l.internalId}`,
        label: l.description || '(sin nombre)'
      };

      const companyLabel = l.companyName || 'Sin compañía';
      if (!byCompany.has(companyLabel)) {
        byCompany.set(companyLabel, []);
      }
      byCompany.get(companyLabel).push(option);

    });

    const groups: ResultGroup[] = [];
    if (companyMatches.length > 0) {
      groups.push({ label: 'Compañías', options: companyMatches });
    }

    Array.from(byCompany.keys()).sort().forEach((label: string) => {
      groups.push({ label: `Licencias · ${label}`, options: byCompany.get(label) });
    });

    this.groups = groups;
  }

  private matchesTokens(tokens: string[], values: Array<string | undefined | null>) {
    const haystack = this.normalize(values.filter((value: string) => !!value).join(' '));
    return tokens.every((token: string) => haystack.includes(token));
  }

  private normalize(value: string) {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

}
